// WP-1.13 (OB-2/3, S-38 `/import`): import-source CRUD + preview/commit mutations.
import {
  importCommitRequestSchema,
  importCommitResponseSchema,
  importPreviewRequestSchema,
  importPreviewResponseSchema,
  importSourceCreateRequestSchema,
  importSourceListResponseSchema,
  importSourceResponseSchema,
  type ImportCommitRequest,
  type ImportCommitResponse,
  type ImportPreviewRequest,
  type ImportPreviewRow,
  type ImportSourceCreateRequest,
  type ImportSourceResponse,
} from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from './api';
import { TRANSACTIONS_KEY } from './transactions';
import { ACCOUNTS_KEY } from './accounts';

export const IMPORT_SOURCES_KEY = ['import-sources'] as const;

export function useImportSources(): UseQueryResult<ImportSourceResponse[]> {
  return useQuery({
    queryKey: IMPORT_SOURCES_KEY,
    queryFn: async () => {
      const body = importSourceListResponseSchema.parse(await api.get('/api/v1/import/sources'));
      return body.sources;
    },
  });
}

export function useCreateImportSource(): UseMutationResult<
  ImportSourceResponse,
  Error,
  ImportSourceCreateRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      const parsed = importSourceCreateRequestSchema.parse(input);
      return importSourceResponseSchema.parse(await api.post('/api/v1/import/sources', parsed));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: IMPORT_SOURCES_KEY });
    },
  });
}

export function usePreviewCsvImport(): UseMutationResult<
  ImportPreviewRow[],
  Error,
  ImportPreviewRequest
> {
  return useMutation({
    mutationFn: async (input) => {
      const parsed = importPreviewRequestSchema.parse(input);
      const body = importPreviewResponseSchema.parse(
        await api.post('/api/v1/import/preview', parsed),
      );
      return body.rows;
    },
  });
}

export function useCommitCsvImport(): UseMutationResult<
  ImportCommitResponse,
  Error,
  ImportCommitRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      const parsed = importCommitRequestSchema.parse(input);
      return importCommitResponseSchema.parse(await api.post('/api/v1/import/commit', parsed));
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: TRANSACTIONS_KEY }),
        queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
      ]);
    },
  });
}
